import {
  addDays, addMonths, addQuarters, subDays, subMonths, subQuarters,
} from 'date-fns';
import { stringify } from '../../utils/methods';

const accountStatus = {
  ACTIVE: 'Active',
  TRIAL: 'Trial',
  EXPIRED: 'Expired',
  CHURNED: 'Churned',
  SUSPENDED: 'Suspended',
};

const featureUsageModeConst = {
  USE: 'Used',
  DONT_USE: 'Not Used',
};

const marks = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 20,
    label: '20',
  },
  {
    value: 40,
    label: '40',
  },
  {
    value: 60,
    label: '60',
  },
  {
    value: 80,
    label: '80',
  },
  {
    value: 100,
    label: '100+',
  },
];

const healthScoreMarks = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 20,
    label: '20',
  },
  {
    value: 40,
    label: '40',
  },
  {
    value: 60,
    label: '60',
  },
  {
    value: 80,
    label: '80',
  },
  {
    value: 100,
    label: '100',
  },
];

const defaultFilterValue = '- all -';

const defaultView = { id: defaultFilterValue, value: defaultFilterValue, name: 'All Accounts' };

const initialFilterValue = {
  licenceAssignment: [],
  licenceStatus: [],
  package: [],
  packageVersion: [],
  salesforceInstance: [],
  accountVisibility: defaultFilterValue,
  country: [],
  language: [],
  organisationEdition: [],
  featureUsageFilter: [],
  featureUsageMode: featureUsageModeConst.USE,
  searchQuery: '',
  range: [0, 150],
  healthScoreRange: [0, 100],
  healthScoreTrend: [],
  toggleAdvanceFilter: false,
  renewalDate: {
    type: null,
    key: null,
    startDate: null,
    endDate: null,
  },
  activationDate: {
    type: null,
    key: null,
    startDate: null,
    endDate: null,
  },
};

const renewalDateRanges = {
  next15Days: {
    label: 'Next 15 Days',
    startDate: new Date(),
    endDate: addDays(new Date(), 15),
  },
  next30Days: {
    label: 'Next 30 Days',
    startDate: new Date(),
    endDate: addDays(new Date(), 30),
  },
  nextQuarter: {
    label: 'Next Quarter',
    startDate: new Date(),
    endDate: addQuarters(new Date(), 1),
  },
  nextHalfYear: {
    label: 'Next Half Year',
    startDate: new Date(),
    endDate: addMonths(new Date(), 6),
  },
};

const activationDateRanges = {
  last15Days: {
    label: 'Last 15 Days',
    startDate: subDays(new Date(), 15),
    endDate: new Date(),
  },
  last30Days: {
    label: 'Last 30 Days',
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  },
  lastQuarter: {
    label: 'Last Quarter',
    startDate: subQuarters(new Date(), 1),
    endDate: new Date(),
  },
  lastHalfYear: {
    label: 'Last Half Year',
    startDate: subMonths(new Date(), 6),
    endDate: new Date(),
  },
  lastYear: {
    label: 'Last Year',
    startDate: subMonths(new Date(), 12),
    endDate: new Date(),
  },
};

const hasDefaultFilters = (selectedFilters) => {
  const res = Object.keys(initialFilterValue).some(
    (key) => initialFilterValue[key] !== selectedFilters[key],
  );
  return !res;
};

const hasAdvanceFilters = (selectedFilters) => {
  const advanceFilters = [
    'activationDate',
    'renewalDate',
    'searchQuery',
    'range',
    'healthScoreRange',
    'healthScoreTrend',
  ];
  const res = advanceFilters.some(
    (key) => stringify(initialFilterValue[key]) !== stringify(selectedFilters[key]),
  );
  return res;
};

const compareVersions = (versionA, versionB) => {
  const partsA = versionA.split('.').map(Number);
  const partsB = versionB.split('.').map(Number);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i] || 0;
    const partB = partsB[i] || 0;

    if (partA !== partB) {
      return partA - partB;
    }
  }

  return 0;
};

const filterByName = (array) => {
  const sortedOptions = array.sort((a, b) => a.name.localeCompare(b.name)).filter(({ name }, index, arr) => index === 0 || name !== arr.at(index - 1).name);
  return sortedOptions;
};

const regionNamesByLanguageCode = new Intl.DisplayNames(['en'], { type: 'language' });
const regionNamesByCountryCode = new Intl.DisplayNames(['en'], { type: 'region' });

const deleteProtectedView = ['- all -'];

const defaultCategories = [
  { title: 'Startup', from: '0', to: '10' },
  { title: 'Small', from: '11', to: '30' },
  { title: 'Medium', from: '31', to: '100' },
  { title: 'Enterprise', from: '100', to: '' },
  { title: 'Site Wide', from: '', to: '' },
];

export {
  accountStatus,
  featureUsageModeConst,
  marks,
  healthScoreMarks,
  defaultView,
  defaultFilterValue,
  initialFilterValue,
  hasDefaultFilters,
  hasAdvanceFilters,
  regionNamesByLanguageCode,
  regionNamesByCountryCode,
  compareVersions,
  filterByName,
  deleteProtectedView,
  defaultCategories,
  renewalDateRanges,
  activationDateRanges,
};
